<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" class="d-flex align-center">
        <h2 class="mr-4">Solicitudes de comercios</h2>
        <v-chip v-if="pendingCount" color="orange" dark small>{{ pendingCount }} por aprobar</v-chip>
        <v-spacer />
        <v-btn text :loading="loading" @click="load()"><v-icon left>mdi-refresh</v-icon> Actualizar</v-btn>
      </v-col>
    </v-row>

    <v-card>
      <v-data-table
        :headers="headers"
        :items="requests"
        :loading="loading"
        sort-by="createdAt"
        sort-desc
        no-data-text="Aún no hay solicitudes"
        @click:row="open"
        style="cursor: pointer"
      >
        <template #item.createdAt="{ item }">{{ formatDate(item.createdAt) }}</template>
        <template #item.status="{ item }">
          <v-chip small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
        </template>
        <template #item.tienda="{ item }">
          <v-chip v-if="item.aiStatus === 'processing'" small color="blue" dark>Generando…</v-chip>
          <v-chip v-else-if="item.aiStatus === 'done'" small color="green" dark>Generada</v-chip>
          <v-chip v-else-if="item.aiStatus === 'error'" small color="red" dark>Error</v-chip>
          <span v-else class="grey--text">—</span>
        </template>
        <template #item.archivos="{ item }">{{ item.cartas.length }} cartas · {{ item.photos.length }} fotos</template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="640" scrollable>
      <v-card v-if="current">
        <v-toolbar flat color="#f6f6f6">
          <v-toolbar-title>{{ current.name }}</v-toolbar-title>
          <v-spacer />
          <v-chip small :color="statusColor(current.status)" dark class="mr-2">{{ statusLabel(current.status) }}</v-chip>
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <div><b>Contacto principal:</b> {{ current.contactName }}</div>
          <div><b>Celular:</b> {{ current.phone }}</div>
          <div><b>Correo:</b> {{ current.email }}</div>
          <div><b>Registrado:</b> {{ formatDate(current.createdAt) }}</div>
          <div v-if="current.note"><b>Nota:</b> {{ current.note }}</div>

          <v-divider class="my-4" />

          <div class="text-subtitle-2 mb-1">Cuenta para pagos</div>
          <div class="mb-2">
            <v-chip small label class="mr-1" :color="current.accountType ? 'green' : 'orange'" dark>{{ current.accountType ? current.accountType : 'Sin cuenta' }}</v-chip>
            <v-chip v-if="current.bankCertification" small class="mr-1" :href="current.bankCertification.url" target="_blank">Certificación bancaria</v-chip>
          </div>
          <div class="text-subtitle-2 mb-1">Cartas ({{ current.cartas.length }})</div>
          <div v-if="!current.cartas.length" class="grey--text mb-2">El comercio no subió carta.</div>
          <v-chip v-for="f in current.cartas" :key="f.url" small class="mr-1 mb-1" :href="f.url" target="_blank">{{ f.name }}</v-chip>

          <div class="text-subtitle-2 mt-3 mb-1">Fotos de platos ({{ current.photos.length }})</div>
          <div v-if="!current.photos.length" class="grey--text mb-2">Sin fotos.</div>
          <v-chip v-for="f in current.photos" :key="f.url" small class="mr-1 mb-1" :href="f.url" target="_blank">{{ f.name }}</v-chip>

          <template v-if="current.status !== 'rejected'">
            <v-file-input v-model="newCartas" class="mt-4" label="Agregar cartas (foto o PDF)" multiple chips small-chips outlined dense accept="image/*,application/pdf" prepend-icon="" prepend-inner-icon="mdi-file-document-outline" />
            <v-file-input v-model="newPhotos" label="Agregar fotos de platos" multiple chips small-chips outlined dense accept="image/*" prepend-icon="" prepend-inner-icon="mdi-camera" />
            <v-btn small :disabled="!newCartas.length && !newPhotos.length" :loading="uploading" @click="addFiles">Subir archivos</v-btn>
          </template>

          <template v-if="current.status === 'approved'">
            <v-divider class="my-4" />
            <div class="text-subtitle-2 mb-2">Tienda virtual</div>

            <v-alert v-if="current.aiStatus === 'processing'" type="info" dense text>
              <v-progress-circular indeterminate size="16" width="2" class="mr-2" /> La IA está leyendo la carta y armando la tienda…
            </v-alert>
            <v-alert v-else-if="current.aiStatus === 'done'" type="success" dense text>{{ current.aiSummary }}</v-alert>
            <v-alert v-else-if="current.aiStatus === 'error'" type="error" dense text>
              No se pudo generar: {{ current.aiError }}
            </v-alert>

            <v-btn
              color="primary"
              :loading="generating"
              :disabled="current.aiStatus === 'processing' || (!current.cartas.length && !current.photos.length)"
              @click="generate"
            >
              <v-icon left>mdi-creation</v-icon> Generar Tienda Virtual IA
            </v-btn>
            <div class="text-caption grey--text mt-1">
              Lee las cartas y fotos subidas y crea categorías y productos. Si ya se generó antes, reemplaza los productos creados por la IA.
            </div>

            <div v-if="current.store" class="mt-4">
              <div>
                <b>Tienda:</b> {{ current.store.name }} ·
                <a :href="'https://web.festyapp.co/store/' + (current.store.slug || current.store.id)" target="_blank" rel="noopener">ver página pública</a>
              </div>
              <v-switch
                :input-value="current.store.status === 'active'"
                :loading="publishing"
                :label="current.store.status === 'active' ? 'Publicada (visible a clientes)' : 'En borrador (no visible)'"
                @change="togglePublish"
              />
            </div>
          </template>
        </v-card-text>

        <v-divider />
        <v-card-actions v-if="current.status === 'pending'" class="pa-4">
          <v-btn color="error" text :loading="reviewing" @click="rejectDialog = true">Rechazar</v-btn>
          <v-spacer />
          <v-btn color="success" :loading="reviewing" @click="review('approve')">Aprobar negocio</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rejectDialog" max-width="420">
      <v-card>
        <v-card-title>Rechazar solicitud</v-card-title>
        <v-card-text>
          <v-textarea v-model="rejectNote" label="Motivo (se le envía al comercio)" outlined rows="3" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="rejectDialog = false">Cancelar</v-btn>
          <v-btn color="error" :loading="reviewing" @click="review('reject', rejectNote)">Rechazar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Parse from 'parse'

const STATUS = {
  pending: ['Por aprobar', 'orange'],
  approved: ['Aprobado', 'green'],
  rejected: ['Rechazado', 'grey']
}

export default {
  name: 'BusinessRequests',
  data: () => ({
    requests: [],
    loading: false,
    dialog: false,
    currentId: null,
    rejectDialog: false,
    rejectNote: '',
    reviewing: false,
    generating: false,
    publishing: false,
    uploading: false,
    newCartas: [],
    newPhotos: [],
    timer: null,
    headers: [
      { text: 'Fecha', value: 'createdAt' },
      { text: 'Negocio', value: 'name' },
      { text: 'Contacto', value: 'contactName' },
      { text: 'Celular', value: 'phone' },
      { text: 'Archivos', value: 'archivos', sortable: false },
      { text: 'Estado', value: 'status' },
      { text: 'Tienda IA', value: 'tienda', sortable: false }
    ]
  }),
  computed: {
    current () {
      return this.requests.find(r => r.id === this.currentId) || null
    },
    pendingCount () {
      return this.requests.filter(r => r.status === 'pending').length
    }
  },
  mounted () {
    this.load()
    this.timer = setInterval(() => {
      if (this.requests.some(r => r.aiStatus === 'processing')) this.load(true)
    }, 6000)
  },
  beforeDestroy () {
    clearInterval(this.timer)
  },
  methods: {
    statusLabel: s => (STATUS[s] || [s])[0],
    statusColor: s => (STATUS[s] || [s, 'grey'])[1],
    formatDate (d) {
      return d ? new Date(d).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' }) : ''
    },
    fail (error, title) {
      this.$snotify.error(`${error && error.message ? error.message : error}`, title, { timeout: 5000, preventDuplicates: true })
    },
    async load (silent) {
      if (!silent) this.loading = true
      try {
        this.requests = await Parse.Cloud.run('getBusinessRequestsBO')
      } catch (error) {
        this.fail(error, 'Solicitudes')
      } finally {
        this.loading = false
      }
    },
    replace (updated) {
      const i = this.requests.findIndex(r => r.id === updated.id)
      if (i >= 0) this.$set(this.requests, i, updated)
      else this.requests.unshift(updated)
    },
    open (item) {
      this.currentId = item.id
      this.newCartas = []
      this.newPhotos = []
      this.dialog = true
    },
    async review (action, note) {
      this.reviewing = true
      try {
        this.replace(await Parse.Cloud.run('reviewBusinessRequestBO', { id: this.currentId, action, note }))
        this.rejectDialog = false
        this.rejectNote = ''
        this.$snotify.success(action === 'approve' ? 'Negocio aprobado y avisado por correo.' : 'Solicitud rechazada.', 'Solicitudes', { timeout: 4000 })
      } catch (error) {
        this.fail(error, 'Solicitudes')
      } finally {
        this.reviewing = false
      }
    },
    async generate () {
      const regenerate = this.current.aiStatus === 'done'
      if (regenerate && !window.confirm('Ya se generó una tienda. Se reemplazarán los productos creados por la IA. ¿Continuar?')) return
      this.generating = true
      try {
        this.replace(await Parse.Cloud.run('generateStoreFromMenuBO', { id: this.currentId }))
      } catch (error) {
        this.fail(error, 'Tienda virtual IA')
      } finally {
        this.generating = false
      }
    },
    async togglePublish () {
      this.publishing = true
      try {
        await Parse.Cloud.run('changeStatusStoreBO', { id: this.current.store.id })
        await this.load(true)
      } catch (error) {
        this.fail(error, 'Publicar tienda')
      } finally {
        this.publishing = false
      }
    },
    async upload (files) {
      const saved = []
      for (const file of files) {
        const safeName = file.name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9._-]+/g, '_')
        const parseFile = new Parse.File(safeName, file)
        await parseFile.save()
        saved.push(parseFile)
      }
      return saved
    },
    async addFiles () {
      this.uploading = true
      try {
        const cartas = await this.upload(this.newCartas)
        const photos = await this.upload(this.newPhotos)
        this.replace(await Parse.Cloud.run('addBusinessFilesBO', { id: this.currentId, cartas, photos }))
        this.newCartas = []
        this.newPhotos = []
      } catch (error) {
        this.fail(error, 'Archivos')
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>
